/**
 * Plan Card Component
 * Displays subscription plan details — matches native app layout
 */
import React from 'react';
import { Check } from 'lucide-react';

const PlanCard = ({ 
  plan, 
  isCurrent = false, 
  isRecommended = false, 
  onSelect,
  isLoading = false,
  billingInterval = 'monthly'
}) => {
  const { name, description, price, annualPrice, billingCycle, pricePerProperty, maxProperties, trialDays, inventoryType } = plan;
  const isAnnual = billingInterval === 'annual';
  const displayPrice = isAnnual && annualPrice && price > 0 ? annualPrice * 12 : price;
  const displayUnit = isAnnual && annualPrice && price > 0 ? '/year' : '/month';
  const monthlySavings = isAnnual && annualPrice && price > 0
    ? Math.round((1 - annualPrice / price) * 100)
    : 0;
  const displayFeatures = plan.displayFeatures || [];
  const isFree = price === 0 && (pricePerProperty === 0 || !pricePerProperty);
  const isHighlighted = isCurrent || isRecommended;
  const singular = inventoryType === 'products' ? 'product' : inventoryType === 'services' ? 'service' : 'property';
  const plural = inventoryType === 'products' ? 'products' : inventoryType === 'services' ? 'services' : 'properties';

  const limits = [];
  if (maxProperties !== null && maxProperties !== undefined) {
    limits.push({
      icon: '🏠',
      text: `${maxProperties === -1 ? 'Unlimited' : `Up to ${maxProperties}`} ${plural} you can manage`
    });
  }
  if (plan.maxLocations !== null && plan.maxLocations !== undefined) {
    limits.push({
      icon: '📍',
      text: `${plan.maxLocations === -1 ? 'Unlimited' : plan.maxLocations} business ${plan.maxLocations === 1 ? 'location' : 'locations'}`
    });
  }

  return (
    <div 
      className={`relative rounded-2xl border-2 p-6 transition-all ${
        isCurrent 
          ? 'border-blue-500 bg-dark-100' 
          : isRecommended
            ? 'border-accent bg-dark-100'
            : 'border-white/10 bg-dark-100 hover:border-white/20'
      }`}
    >
      {/* Badges */}
      {isCurrent && (
        <div className="absolute -top-3 right-6">
          <div className="px-3 py-1 bg-blue-500 rounded-full">
            <span className="text-xs font-bold text-white tracking-wide">CURRENT PLAN</span>
          </div>
        </div>
      )}
      {isRecommended && !isCurrent && (
        <div className="absolute -top-3 right-6">
          <div className="px-3 py-1 bg-accent rounded-full">
            <span className="text-xs font-bold text-dark tracking-wide">RECOMMENDED</span>
          </div>
        </div>
      )}

      {/* Plan Name & Description */}
      <h3 className="text-2xl font-bold text-white mb-1">{name}</h3>
      {description && (
        <p className="text-sm text-gray-400 mb-4">{description}</p>
      )}

      {/* PRICING Section */}
      <div className="mb-4 pb-4 border-b border-white/10">
        <p className="text-[11px] font-bold text-gray-500 tracking-widest mb-2">PRICING</p>
        <div className="flex items-end gap-1">
          <span className="text-xl text-white">$</span>
          <span className="text-4xl font-bold text-white leading-none">{displayPrice}</span>
          <span className="text-gray-400 mb-1 ml-1">{displayUnit}</span>
          {isFree && <span className="text-accent font-bold ml-2 mb-1">FREE</span>}
        </div>
        {isAnnual && !isFree && annualPrice && (
          <p className="text-xs text-gray-500 mt-1">Renews annually</p>
        )}
        {isAnnual && monthlySavings > 0 && (
          <p className="text-sm text-gray-400 mt-2">
            💰 ${annualPrice}/mo · Save {monthlySavings}% vs monthly
          </p>
        )}
      </div>

      {/* Trial Badge */}
      {trialDays > 0 && (
        <div className="mb-4 inline-block bg-accent/10 rounded-lg px-3 py-2">
          <span className="text-sm font-semibold text-accent">
            {trialDays}-day free trial included
          </span>
        </div>
      )}

      {/* WHAT'S INCLUDED */}
      {(limits.length > 0 || displayFeatures.length > 0) && (
        <div className="mb-5">
          <p className="text-[11px] font-bold text-gray-500 tracking-widest mb-3">WHAT'S INCLUDED</p>
          {limits.map((limit, index) => (
            <div key={`limit-${index}`} className="flex items-center gap-2.5 mb-2.5">
              <span className="text-base w-6 text-center">{limit.icon}</span>
              <span className="text-sm text-gray-300">{limit.text}</span>
            </div>
          ))}
          {displayFeatures.map((feature, index) => {
            const text = typeof feature === 'string' ? feature : (feature.text || feature);
            const isComingSoon = typeof feature === 'object' && feature.coming_soon;
            return (
              <div key={`feat-${index}`} className="flex items-start gap-2.5 mb-2.5">
                <Check size={16} className="text-accent mt-0.5 shrink-0" />
                <span className="text-sm text-gray-300 flex items-center gap-1.5 flex-wrap">
                  {text}
                  {isComingSoon && (
                    <span className="inline-block text-[9px] font-medium tracking-wide text-gray-500 bg-white/10 border border-white/10 rounded-full px-1.5 py-[1px] leading-tight whitespace-nowrap">
                      coming soon
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Button */}
      <button
        onClick={() => onSelect && onSelect(plan)}
        disabled={isCurrent || isLoading}
        className={`w-full py-3 rounded-xl font-semibold transition-all ${
          isCurrent
            ? 'bg-blue-500/10 border border-blue-500 text-blue-500 cursor-not-allowed'
            : isRecommended
              ? 'bg-accent text-dark hover:shadow-lg hover:shadow-accent/30'
              : 'bg-dark-200 text-white hover:bg-dark-200/80'
        }`}
      >
        {isLoading ? 'Processing...' : isCurrent ? 'Current Plan' : isFree ? 'Get started free' : trialDays > 0 ? `Start ${trialDays}-day free trial` : 'Select Plan'}
      </button>
    </div>
  );
};

export default PlanCard;
